import * as Logger from './logger.js';
import { HSComponent } from './component.js';
import { escapeHtmlAttribute } from './utils.js';
import './toolbar-button.js';
import styles from './ml-models.css';
import pipelineStyles from './pipeline-sandbox.css';
import sharedPanelStyles from './ml-panel-shared.css';
import template from './ml-models.html';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent, GridComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, TitleComponent, TooltipComponent, GridComponent, CanvasRenderer]);

// Model Management — registry, versions, lifecycle, training jobs, evaluation,
// shadow comparison. See docs/ML_FRONTEND_BUILD_PLAN.md §2 (surface 2).
//
// Response shapes are frozen in docs/ML_API_SPECIFICATION.md §4 (confirmed via
// ../elm_processor_proto/docs/handoff/frontend/model-management-real-shapes.md) —
// built directly against real field names, no defensive fallbacks. Two things NOT
// covered by that freeze, left as best-effort: `GET /models/:name/versions`'
// response shape (version/state/created_at), and the `POST /training/jobs`
// *request* body (only the response shape is frozen) — both noted inline.

const LIFECYCLE_ALIASES = ['latest', 'stable', 'canary'];
const SHADOW_DISPLAY_CAP = 50;   // disagreements[] can be large; show a capped page client-side too

export class HSMlModels extends HSComponent {
    #baseUrl = '/elm/api/ml';
    #view = 'list';
    #models = [];
    #modelsLoaded = false;
    #modelsError = null;
    #selectedModelName = null;
    #versions = [];
    #versionsLoaded = false;
    #versionsError = null;
    #selectedVersion = null;
    #versionDetail = null;
    #versionDetailError = null;
    #evaluation = null;
    #evaluationError = null;
    #evaluationLoading = false;
    #trainingJobs = [];
    #trainingJobsLoaded = false;
    #trainingJobsError = null;
    #shadow = null;
    #shadowError = null;
    #shadowLoading = false;
    #actionStatus = null;
    #calibrationChart = null;
    #capabilities = null;    // GET /elm/api/sandbox/capabilities — kernels[].params drives the Tuning form
    #manifestProvenance = null; // { feature, bundle_id } — only ever fetched for the LATEST version, see #selectVersion

    get baseUrl() { return this.#baseUrl; }
    set baseUrl(v) { this.#baseUrl = v || '/elm/api/ml'; }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(pipelineStyles);
        this.loadStyles(sharedPanelStyles);
        this.loadStyles(styles);
        this.#bindEvents();
        this.#fetchModels();
        this.#fetchCapabilities();
    }

    onUnload() {
        super.onUnload();
        this.#calibrationChart?.dispose();
    }

    #bindEvents() {
        const sd = this.shadowRoot;
        this.onEvent('click', () => this.#close(), sd.getElementById('mlm-exit-btn'));
        this.onEvent('click', () => this.#fetchModels(), sd.getElementById('mlm-refresh-btn'));
        this.onEvent('click', () => this.#backToList(), sd.getElementById('mlm-back-btn'));
    }

    #close() {
        this.emit({ type: 'hs-close-ml-models' });
    }

    #showView(name) {
        this.#view = name;
        const sd = this.shadowRoot;
        for (const el of sd.querySelectorAll('.ps-view')) el.classList.add('hidden');
        sd.getElementById(`mlm-${name}-view`)?.classList.remove('hidden');
    }

    #backToList() {
        this.#calibrationChart?.dispose();
        this.#calibrationChart = null;
        this.#showView('list');
        this.#fetchModels();
    }

    #esc(str) {
        return escapeHtmlAttribute(str);
    }

    #formatTs(ms) {
        return typeof ms === 'number' ? new Date(ms).toLocaleString() : '—';
    }

    // Dev-mode-only endpoint (docs/FEATURE_BUNDLE_EXCHANGE_DESIGN.md § 6) — absent outside the
    // sandbox, so the Tuning section just falls back to a raw-JSON-only note when it 404/501s.
    async #fetchCapabilities() {
        try {
            this.#capabilities = await this.fetch('/elm/api/sandbox/capabilities');
        } catch (err) {
            Logger.debug('capabilities fetch failed (expected outside dev mode): ', err);
            this.#capabilities = null;
        }
    }

    // ---- List ----

    async #fetchModels() {
        this.#modelsLoaded = false;
        this.#renderList();
        try {
            const res = await this.fetch(`${this.#baseUrl}/models`);
            this.#models = res?.models || [];
            this.#modelsError = null;
        } catch (err) {
            Logger.error('Failed to fetch ML models: ', err);
            this.#models = [];
            this.#modelsError = err;
        }
        this.#modelsLoaded = true;
        this.#renderList();
    }

    #renderList() {
        const el = this.shadowRoot.getElementById('mlm-model-table');
        if (!el) return;
        if (!this.#modelsLoaded) {
            el.innerHTML = '<div class="mlw-placeholder">Loading models…</div>';
            return;
        }
        if (this.#modelsError) {
            el.innerHTML = `<div class="mlw-error-row">Failed to load models: ${this.#esc(this.#modelsError.message || String(this.#modelsError))}</div>`;
            return;
        }
        if (!this.#models.length) {
            el.innerHTML = '<div class="mlw-placeholder">No models registered yet.</div>';
            return;
        }
        const rows = this.#models.map(m => {
            const states = Object.entries(m.states || {}).map(([s, count]) => `<span class="mlm-state-badge ${this.#esc(s)}">${this.#esc(s)}: ${count}</span>`).join(' ') || '—';
            const aliases = Object.entries(m.aliases || {}).map(([a, v]) => `${a}→${v}`).join(', ') || '—';
            return `<tr class="mlm-row-clickable" data-name="${this.#esc(m.name)}">
                <td>${this.#esc(m.name)}</td>
                <td>${this.#esc(m.latest_version)}</td>
                <td>${states}</td>
                <td>${this.#esc(aliases)}</td>
            </tr>`;
        }).join('');
        el.innerHTML = `<table class="mlm-table">
            <thead><tr><th>Name</th><th>Latest version</th><th>States</th><th>Aliases</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>`;
        for (const row of el.querySelectorAll('tr[data-name]')) {
            this.onEvent('click', () => this.#openModelDetail(row.dataset.name), row);
        }
    }

    // ---- Detail (versions + lifecycle + evaluation + training) ----

    #openModelDetail(name) {
        this.#selectedModelName = name;
        this.#selectedVersion = null;
        this.#versionDetail = null;
        this.#evaluation = null;
        this.#shadow = null;
        this.#actionStatus = null;
        this.shadowRoot.getElementById('mlm-detail-name').textContent = name;
        this.#showView('detail');
        this.#fetchVersions();
        this.#fetchTrainingJobs();
        this.#renderVersionDetail();
    }

    async #fetchVersions() {
        this.#versionsLoaded = false;
        this.#renderVersionsList();
        try {
            const res = await this.fetch(`${this.#baseUrl}/models/${encodeURIComponent(this.#selectedModelName)}/versions`);
            this.#versions = res?.versions || [];
            this.#versionsError = null;
        } catch (err) {
            Logger.error('Failed to fetch model versions: ', err);
            this.#versions = [];
            this.#versionsError = err;
        }
        this.#versionsLoaded = true;
        this.#renderVersionsList();
    }

    #renderVersionsList() {
        const el = this.shadowRoot.getElementById('mlm-versions-table');
        if (!el) return;
        if (!this.#versionsLoaded) {
            el.innerHTML = '<div class="mlw-placeholder">Loading versions…</div>';
            return;
        }
        if (this.#versionsError) {
            el.innerHTML = `<div class="mlw-error-row">Failed to load versions: ${this.#esc(this.#versionsError.message || String(this.#versionsError))}</div>`;
            return;
        }
        if (!this.#versions.length) {
            el.innerHTML = '<div class="mlw-placeholder">No versions yet.</div>';
            return;
        }
        const rows = this.#versions.map(v => {
            const selected = this.#selectedVersion === v.version ? ' mlm-row-selected' : '';
            const aliases = (v.aliases || []).map(a => `@${a}`).join(', ') || '—';
            return `<tr class="mlm-row-clickable${selected}" data-version="${this.#esc(v.version)}">
                <td>${this.#esc(v.version)}</td>
                <td><span class="mlm-state-badge ${this.#esc(v.state || '')}">${this.#esc(v.state || '—')}</span></td>
                <td>${this.#esc(aliases)}</td>
                <td>${this.#esc(v.evaluation_count)}</td>
                <td>${v.has_artifact ? 'yes' : 'no'}</td>
                <td>${this.#formatTs(v.created_ts)}</td>
            </tr>`;
        }).join('');
        el.innerHTML = `<table class="mlm-table">
            <thead><tr><th>Version</th><th>State</th><th>Aliases</th><th>Evaluations</th><th>Artifact</th><th>Created</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>`;
        for (const row of el.querySelectorAll('tr[data-version]')) {
            this.onEvent('click', () => this.#selectVersion(row.dataset.version), row);
        }
    }

    #modelRef(version) {
        return encodeURIComponent(`${this.#selectedModelName}@${version}`);
    }

    async #selectVersion(version) {
        this.#selectedVersion = version;
        this.#actionStatus = null;
        this.#shadow = null;
        this.#manifestProvenance = null;
        this.#renderVersionsList();
        this.#renderVersionDetail();
        try {
            this.#versionDetail = await this.fetch(`${this.#baseUrl}/models/${this.#modelRef(version)}`);
            this.#versionDetailError = null;
        } catch (err) {
            Logger.error('Failed to fetch version detail: ', err);
            this.#versionDetail = null;
            this.#versionDetailError = err;
        }
        // GET /models/:ref never returns feature/bundle_id (confirmed against lib/ml_api.js —
        // serialize_model_entry omits them); only GET /manifests/:name has them, and only for
        // the LATEST version. Fetch it only when this really is the latest, so an older version
        // is never shown someone else's provenance.
        const isLatest = this.#versions[0] && String(this.#versions[0].version) === String(version);
        if (isLatest) {
            try {
                const m = await this.fetch(`${this.#baseUrl}/manifests/${encodeURIComponent(this.#selectedModelName)}`);
                this.#manifestProvenance = { feature: m?.feature || null, bundle_id: m?.bundle_id || null };
            } catch (err) {
                Logger.debug('manifest provenance fetch failed (dev-mode only endpoint): ', err);
            }
        }
        this.#renderVersionDetail();
        this.#fetchEvaluation(version);
    }

    #renderVersionDetail() {
        const el = this.shadowRoot.getElementById('mlm-version-detail');
        if (!el) return;
        if (!this.#selectedVersion) {
            el.innerHTML = '<div class="mlw-placeholder">Select a version to see lifecycle actions and evaluation.</div>';
            return;
        }
        if (this.#versionDetailError) {
            el.innerHTML = `<div class="mlw-error-row">Failed to load ${this.#esc(this.#selectedVersion)}: ${this.#esc(this.#versionDetailError.message || String(this.#versionDetailError))}</div>`;
            return;
        }
        const d = this.#versionDetail || {};
        const state = d.state || '—';
        // `aliases` here is the list of alias names pointing AT this version —
        // distinct from the list view's { alias: version } map.
        const aliasesHere = (d.aliases || []).map(a => `@${a}`).join(', ') || 'none';
        const evalHistory = (d.evaluations || []).map(ev =>
            `<div class="mlm-kv-row"><span>dataset ${this.#esc(ev.dataset_version)}</span><span>${this.#formatTs(ev.evaluated_at)}</span></div>`
        ).join('') || '<div class="mlw-placeholder">No evaluation history.</div>';

        // evaluation_count gates promotion server-side (ModelRegistry.promote() throws
        // when entry.evaluations.length === 0) — disable client-side too, without a
        // second request, per the versions-list summary's documented purpose for this field.
        const summary = this.#versions.find(v => String(v.version) === String(this.#selectedVersion));
        const evaluationCount = summary?.evaluation_count ?? (d.evaluations || []).length;
        const promoteDisabled = evaluationCount === 0;
        const tuningHtml = this.#renderTuningSection(d);
        const compareOptions = this.#versions
            .filter(v => String(v.version) !== String(this.#selectedVersion))
            .map(v => `<option value="${this.#esc(v.version)}">v${this.#esc(v.version)} (${this.#esc(v.state || '')})</option>`)
            .join('');
        const isLatestVersion = this.#versions[0] && String(this.#versions[0].version) === String(this.#selectedVersion);
        const provenanceRow = this.#manifestProvenance && (this.#manifestProvenance.feature || this.#manifestProvenance.bundle_id)
            ? `<div class="mlm-kv-row"><span>Feature</span><span class="ps-feature-badge" title="${this.#manifestProvenance.bundle_id ? `bundle_id ${this.#esc(this.#manifestProvenance.bundle_id)}` : 'No bundle_id'}">${this.#manifestProvenance.feature ? `📦 ${this.#esc(this.#manifestProvenance.feature)}` : '📦 (unassigned)'}</span></div>`
            : (isLatestVersion ? '' : '<div class="mlm-kv-row"><span>Feature</span><span class="ps-form-hint">provenance is only readable for the latest version</span></div>');

        el.innerHTML = `
            <div class="mlm-section">
                <div class="mlm-section-title">${this.#esc(this.#selectedModelName)}@${this.#esc(this.#selectedVersion)}</div>
                ${provenanceRow}
                <div class="mlm-kv-row"><span>State</span><span class="mlm-state-badge ${this.#esc(state)}">${this.#esc(state)}</span></div>
                <div class="mlm-kv-row"><span>Aliases pointing here</span><span>${this.#esc(aliasesHere)}</span></div>
                <div class="mlm-kv-row"><span>Lineage</span><span>${d.lineage === null || d.lineage === undefined ? 'not set' : this.#esc(JSON.stringify(d.lineage))}</span></div>
                <div class="mlm-kv-row"><span>Calibration</span><span>${d.calibration === null || d.calibration === undefined ? 'not set' : this.#esc(JSON.stringify(d.calibration))}</span></div>
                <div class="mlm-actions-row">
                    <button id="mlm-promote-btn" class="btn btn-sm"${promoteDisabled ? ' disabled' : ''} title="${promoteDisabled ? 'No evaluation recorded for this version yet' : ''}">Promote</button>
                    <button id="mlm-retire-btn" class="btn btn-sm btn-danger">Retire</button>
                    <select id="mlm-alias-select" class="ps-input ps-input-sm">
                        ${LIFECYCLE_ALIASES.map(a => `<option value="${a}">@${a}</option>`).join('')}
                    </select>
                    <button id="mlm-alias-btn" class="btn btn-sm">Set alias</button>
                </div>
                <div id="mlm-action-status" class="mlw-preview-status"></div>
            </div>
            <div class="mlm-section">
                <div class="mlw-param-label">Evaluation history</div>
                ${evalHistory}
            </div>
            <div class="mlm-section">
                <div class="mlm-section-title">Tuning</div>
                ${tuningHtml}
            </div>
            <div class="mlm-section">
                <div class="mlm-section-title">Compare</div>
                <div class="ps-form-row">
                    <label>Compare with</label>
                    <select id="mlm-compare-select" class="ps-input ps-input-sm">
                        <option value="">(choose a version)</option>
                        ${compareOptions}
                    </select>
                    <button id="mlm-compare-btn" class="btn btn-sm">Compare</button>
                </div>
                <div id="mlm-compare-body"></div>
            </div>
            <div class="mlm-section">
                <div class="mlm-section-title">Evaluation</div>
                <div id="mlm-evaluation-body"></div>
            </div>
            <div class="mlm-section">
                <div class="mlm-section-title">Shadow comparison</div>
                <button id="mlm-shadow-load-btn" class="btn btn-sm">Load shadow comparison</button>
                <div id="mlm-shadow-body"></div>
            </div>
            <div class="mlm-section">
                <div class="mlm-section-title">Training jobs</div>
                <div class="mlm-training-form">
                    <input type="text" id="mlm-training-dataset" class="ps-input ps-input-sm" placeholder="dataset ref, e.g. overnight_absence_labels@v7" />
                    <button id="mlm-training-submit-btn" class="btn btn-sm">Submit training job</button>
                </div>
                <div id="mlm-training-jobs-body"></div>
            </div>
        `;

        this.onEvent('click', () => this.#promote(), this.shadowRoot.getElementById('mlm-promote-btn'));
        this.onEvent('click', () => this.#retire(), this.shadowRoot.getElementById('mlm-retire-btn'));
        this.onEvent('click', () => this.#setAlias(), this.shadowRoot.getElementById('mlm-alias-btn'));
        this.onEvent('click', () => this.#saveManifestTuning(), this.shadowRoot.getElementById('mlm-tune-save-btn'));
        this.onEvent('click', () => this.#compareVersions(), this.shadowRoot.getElementById('mlm-compare-btn'));
        this.onEvent('click', () => this.#fetchShadow(), this.shadowRoot.getElementById('mlm-shadow-load-btn'));
        this.onEvent('click', () => this.#submitTrainingJob(), this.shadowRoot.getElementById('mlm-training-submit-btn'));

        this.#renderActionStatus();
        this.#renderEvaluation();
        this.#renderShadow();
        this.#renderTrainingJobs();
    }

    // ---- Tuning (index-driven, § 1/§ 2 of sandbox-refocus-view-tune.md) ----
    // Kernel params carry a real type/required/range schema (lib/ml/kernels/metadata.js) —
    // unlike plugin `options[]`, which is name+help text only. Genuinely index-driven, no
    // hand-coded per-kernel form here.

    #renderTuningSection(d) {
        const kernel = d.manifest?.kernel;
        if (!kernel) return '<div class="ps-form-hint">This manifest has no `kernel` (Tier-1/artifact-based) — nothing to tune here yet.</div>';
        const kernelSchema = this.#capabilities?.kernels?.find(k => k.id === kernel);
        if (!kernelSchema) {
            return `<div class="ps-form-hint">No params schema for kernel <code>${this.#esc(kernel)}</code> in the capability index${this.#capabilities ? '' : ' (index unavailable — dev mode only)'}.</div>`;
        }
        const fields = Object.entries(kernelSchema.params || {})
            .map(([key, schema]) => this.#renderKernelParamField(key, schema, d.manifest?.params?.[key]))
            .join('');
        return `<div id="mlm-tune-fields">${fields || '<div class="ps-form-hint">Kernel takes no params.</div>'}</div>
            <div class="ps-form-hint">Saving registers a NEW version via <code>PUT /ml/manifests/:name</code> — never an in-place patch. Evidence tied to the previous version becomes stale until re-run.</div>
            <button id="mlm-tune-save-btn" class="btn btn-sm btn-primary">Save as new version</button>`;
    }

    #renderKernelParamField(key, schema, value) {
        const req = schema.required ? '<span class="ps-cfg-required">*</span>' : '';
        const label = `<label>${this.#esc(key)}${req}</label>`;
        if (schema.type === 'enum') {
            const opts = (schema.values || []).map(v => `<option value="${this.#esc(v)}"${String(value) === String(v) ? ' selected' : ''}>${this.#esc(v)}</option>`).join('');
            return `<div class="ps-form-row">${label}<select class="ps-input ps-input-sm mlm-tune-field" data-key="${this.#esc(key)}">${opts}</select></div>`;
        }
        if (schema.type === 'number') {
            const min = schema.min ?? schema.exclusive_min;
            return `<div class="ps-form-row">${label}<input type="number" class="ps-input ps-input-sm mlm-tune-field" data-key="${this.#esc(key)}" data-type="number" value="${value ?? ''}"${min !== undefined ? ` min="${min}"` : ''} /></div>`;
        }
        if (schema.type === 'array') {
            const arr = Array.isArray(value) ? value.join(', ') : (value ?? '');
            return `<div class="ps-form-row">${label}<input type="text" class="ps-input ps-input-sm mlm-tune-field" data-key="${this.#esc(key)}" data-type="array" placeholder="comma-separated" value="${this.#esc(arr)}" /></div>`;
        }
        if (schema.type === 'duration') {
            return `<div class="ps-form-row">${label}<input type="text" class="ps-input ps-input-sm mlm-tune-field" data-key="${this.#esc(key)}" placeholder="e.g. 5m, 1h" value="${this.#esc(value ?? '')}" /></div>`;
        }
        // string / any
        return `<div class="ps-form-row">${label}<input type="text" class="ps-input ps-input-sm mlm-tune-field" data-key="${this.#esc(key)}" value="${this.#esc(value ?? '')}" /></div>`;
    }

    #readKernelParamsForm(container, paramsSchema) {
        const params = {};
        for (const el of container.querySelectorAll('.mlm-tune-field')) {
            const key = el.dataset.key;
            const schema = paramsSchema[key] || {};
            let v = el.value;
            if (schema.type === 'number') { v = v === '' ? undefined : Number(v); }
            else if (el.dataset.type === 'array') { v = v.split(',').map(s => s.trim()).filter(Boolean); }
            if (v !== undefined && v !== '') params[key] = v;
        }
        return params;
    }

    async #saveManifestTuning() {
        const container = this.shadowRoot.getElementById('mlm-tune-fields');
        const kernel = this.#versionDetail?.manifest?.kernel;
        const kernelSchema = this.#capabilities?.kernels?.find(k => k.id === kernel);
        if (!container || !kernelSchema) return;
        const params = this.#readKernelParamsForm(container, kernelSchema.params || {});
        if (!window.confirm(`Save tuned params as a new version of ${this.#selectedModelName}? Evidence tied to the previous version will need to be re-run.`)) return;

        try {
            await this.fetch(`${this.#baseUrl}/manifests/${encodeURIComponent(this.#selectedModelName)}`, {
                method: 'PUT',
                payload: { manifest: { ...this.#versionDetail.manifest, params } },
            });
            this.#actionStatus = { ok: true, message: 'Saved as a new version.' };
            await this.#fetchVersions();
            const latest = Math.max(...this.#versions.map(v => Number(v.version)));
            await this.#selectVersion(latest);
        } catch (err) {
            Logger.error('Manifest tuning save failed: ', err);
            this.#actionStatus = { ok: false, message: `Save failed: ${err.message || err}` };
            this.#renderActionStatus();
        }
    }

    // ---- Compare (§ 2: "tuning without comparison is guessing") ----

    async #compareVersions() {
        const otherVersion = this.shadowRoot.getElementById('mlm-compare-select')?.value;
        const body = this.shadowRoot.getElementById('mlm-compare-body');
        if (!body) return;
        if (!otherVersion) { body.innerHTML = ''; return; }
        body.innerHTML = '<div class="mlw-placeholder">Loading…</div>';
        try {
            const other = await this.fetch(`${this.#baseUrl}/models/${this.#modelRef(otherVersion)}`);
            body.innerHTML = this.#renderParamsDiff(
                this.#versionDetail?.manifest?.params || {}, other?.manifest?.params || {},
                this.#selectedVersion, otherVersion,
            );
        } catch (err) {
            Logger.error('Compare fetch failed: ', err);
            body.innerHTML = `<div class="mlw-error-row">Compare failed: ${this.#esc(err.message || String(err))}</div>`;
        }
    }

    #renderParamsDiff(a, b, labelA, labelB) {
        const keys = Array.from(new Set([...Object.keys(a || {}), ...Object.keys(b || {})])).sort();
        if (!keys.length) return '<div class="ps-empty-state">No params to compare.</div>';
        const rows = keys.map(k => {
            const av = JSON.stringify(a[k]);
            const bv = JSON.stringify(b[k]);
            return `<div class="ps-bundle-row${av !== bv ? ' ps-bundle-row-warn' : ''}">
                <span class="ps-bundle-row-type">${this.#esc(k)}</span>
                <code>v${this.#esc(labelA)}: ${this.#esc(av ?? '—')}</code>
                <code>v${this.#esc(labelB)}: ${this.#esc(bv ?? '—')}</code>
            </div>`;
        }).join('');
        return `<div class="ps-bundle-rows">${rows}</div>`;
    }

    #renderActionStatus() {
        const el = this.shadowRoot.getElementById('mlm-action-status');
        if (!el) return;
        if (!this.#actionStatus) { el.textContent = ''; el.className = 'mlw-preview-status'; return; }
        el.textContent = this.#actionStatus.message;
        el.className = `mlw-preview-status${this.#actionStatus.ok ? '' : ' mlw-error-row'}`;
    }

    // ---- Lifecycle actions ----

    async #promote() {
        if (!window.confirm(`Promote ${this.#selectedModelName}@${this.#selectedVersion}? This requires a recorded evaluation run.`)) return;
        await this.#runLifecycleAction('promote', `${this.#baseUrl}/models/${this.#modelRef(this.#selectedVersion)}/promote`, { method: 'POST' });
    }

    async #retire() {
        if (!window.confirm(`Retire ${this.#selectedModelName}@${this.#selectedVersion}?`)) return;
        await this.#runLifecycleAction('retire', `${this.#baseUrl}/models/${this.#modelRef(this.#selectedVersion)}/retire`, { method: 'POST' });
    }

    async #setAlias() {
        const alias = this.shadowRoot.getElementById('mlm-alias-select')?.value;
        if (!alias) return;
        if (!window.confirm(`Point @${alias} at ${this.#selectedModelName}@${this.#selectedVersion}?`)) return;
        await this.#runLifecycleAction('set alias', `${this.#baseUrl}/models/${encodeURIComponent(this.#selectedModelName)}/aliases/${encodeURIComponent(alias)}`, {
            method: 'PUT',
            payload: { version: this.#selectedVersion },
        });
    }

    async #runLifecycleAction(label, url, opts) {
        try {
            await this.fetch(url, { ...opts, expectedStatuses: [400, 409] });
            this.#actionStatus = { ok: true, message: `${label} succeeded.` };
            await this.#fetchVersions();
            await this.#selectVersion(this.#selectedVersion);
            await this.#fetchModels();
        } catch (err) {
            Logger.error(`Lifecycle action "${label}" failed: `, err);
            // Promotion fails two distinct ways, both 409 (wrong lifecycle state vs. no
            // evaluation recorded) with distinct messages — surface the real message
            // rather than a generic "not ready", so the user knows which it is.
            let reason = err.message || String(err);
            if (err.status === 400 || err.status === 409) {
                try {
                    const body = await err.response.json();
                    reason = body?.message || body?.error || reason;
                } catch { /* no JSON body */ }
            }
            this.#actionStatus = { ok: false, message: `${label} failed: ${reason}` };
            this.#renderActionStatus();
        }
    }

    // ---- Evaluation ----

    async #fetchEvaluation(version) {
        this.#evaluationLoading = true;
        this.#renderEvaluation();
        try {
            this.#evaluation = await this.fetch(`${this.#baseUrl}/models/${this.#modelRef(version)}/evaluation`);
            this.#evaluationError = null;
        } catch (err) {
            Logger.debug('Evaluation fetch failed (may not have run yet): ', err);
            this.#evaluation = null;
            this.#evaluationError = err;
        }
        this.#evaluationLoading = false;
        if (this.#selectedVersion === version) this.#renderEvaluation();
    }

    #renderEvaluation() {
        const el = this.shadowRoot.getElementById('mlm-evaluation-body');
        if (!el) return;
        if (this.#evaluationLoading) { el.innerHTML = '<div class="mlw-placeholder">Loading evaluation…</div>'; return; }
        if (this.#evaluationError) { el.innerHTML = '<div class="mlw-placeholder">No evaluation run recorded for this version yet.</div>'; return; }
        if (!this.#evaluation) { el.innerHTML = '<div class="mlw-placeholder">No evaluation data.</div>'; return; }

        const e = this.#evaluation;
        // precision/recall/f1 are null on a zero denominator — a real case on sparse
        // datasets, not an error. Render the absence, never as 0.
        const metric = (label, key) => `<div class="mlm-kv-row"><span>${label}</span><span>${e[key] === null || e[key] === undefined ? 'not measurable (zero denominator)' : e[key]}</span></div>`;
        const c = e.confusion_counts || {};
        el.innerHTML = `
            <div class="mlm-kv-row"><span>Dataset</span><span>${this.#esc(e.dataset_ref)}</span></div>
            <div class="mlm-kv-row"><span>Label count</span><span>${this.#esc(e.label_count)}</span></div>
            ${metric('Precision', 'precision')}
            ${metric('Recall', 'recall')}
            ${metric('F1', 'f1')}
            <div class="mlm-kv-row"><span>Evaluated</span><span>${this.#formatTs(e.evaluated_at)}</span></div>
            <div class="mlw-param-label">Confusion counts</div>
            <div class="mlm-kv-row"><span>TP</span><span>${this.#esc(c.tp)}</span></div>
            <div class="mlm-kv-row"><span>FP</span><span>${this.#esc(c.fp)}</span></div>
            <div class="mlm-kv-row"><span>FN</span><span>${this.#esc(c.fn)}</span></div>
            <div class="mlm-kv-row"><span>TN</span><span>${this.#esc(c.tn)}</span></div>
            ${Array.isArray(e.calibration_curve) && e.calibration_curve.length ? '<div class="mlw-param-label">Calibration curve</div><div id="mlm-calibration-chart" class="mlm-chart-container"></div>' : ''}
        `;

        if (Array.isArray(e.calibration_curve) && e.calibration_curve.length) this.#drawCalibrationChart(e.calibration_curve);
    }

    // predicted/observed per spec §4 — points are dropped only if either value is
    // null (rendered as absent, never plotted as 0).
    #drawCalibrationChart(points) {
        const container = this.shadowRoot.getElementById('mlm-calibration-chart');
        if (!container) return;
        const plottable = points.filter(p => p.predicted !== null && p.observed !== null);
        this.#calibrationChart?.dispose();
        this.#calibrationChart = echarts.init(container);
        this.#calibrationChart.setOption({
            tooltip: {
                trigger: 'item',
                formatter: (p) => `predicted ${p.data[0]} vs observed ${p.data[1]}<br/>count: ${plottable[p.dataIndex]?.count ?? '—'}`,
            },
            grid: { left: 40, right: 16, top: 16, bottom: 30 },
            xAxis: { type: 'value', name: 'predicted' },
            yAxis: { type: 'value', name: 'observed' },
            series: [{ type: 'line', data: plottable.map(p => [p.predicted, p.observed]) }],
        });
    }

    // ---- Shadow comparison ----
    // Previously deferred (no field names in the design doc); now specified in
    // spec §4 as the exact return of ShadowRunner.report(). Loaded on demand —
    // disagreements[] is an unbounded array server-side.

    async #fetchShadow() {
        this.#shadowLoading = true;
        this.#renderShadow();
        try {
            this.#shadow = await this.fetch(`${this.#baseUrl}/models/${this.#modelRef(this.#selectedVersion)}/shadow`);
            this.#shadowError = null;
        } catch (err) {
            Logger.error('Shadow comparison fetch failed: ', err);
            this.#shadow = null;
            this.#shadowError = err;
        }
        this.#shadowLoading = false;
        this.#renderShadow();
    }

    #renderShadow() {
        const el = this.shadowRoot.getElementById('mlm-shadow-body');
        if (!el) return;
        if (this.#shadowLoading) { el.innerHTML = '<div class="mlw-placeholder">Loading shadow comparison…</div>'; return; }
        if (this.#shadowError) { el.innerHTML = '<div class="mlw-placeholder">No shadow comparison available.</div>'; return; }
        if (!this.#shadow) { el.innerHTML = ''; return; }

        const s = this.#shadow;
        // agreement_rate is null when comparisons is 0 — a real state, not an error.
        const agreement = s.agreement_rate === null || s.agreement_rate === undefined ? 'not yet compared' : `${(s.agreement_rate * 100).toFixed(1)}%`;
        const dist = s.score_distribution || {};
        const shown = Math.min(SHADOW_DISPLAY_CAP, (s.disagreements || []).length);
        const rows = (s.disagreements || []).slice(0, SHADOW_DISPLAY_CAP).map(d => `
            <tr>
                <td>${this.#esc(d.entity_key)}</td>
                <td>${this.#formatTs(d.boundary_ts)}</td>
                <td>${d.primary_detected ? 'yes' : 'no'}</td>
                <td>${d.shadow_detected ? 'yes' : 'no'}</td>
                <td>${this.#esc(d.score)}</td>
            </tr>`).join('');

        el.innerHTML = `
            <div class="mlm-kv-row"><span>Comparisons</span><span>${this.#esc(s.comparisons)}</span></div>
            <div class="mlm-kv-row"><span>Agreement rate</span><span>${agreement}</span></div>
            <div class="mlm-kv-row"><span>Score distribution</span><span>min ${this.#esc(dist.min)} · max ${this.#esc(dist.max)} · mean ${this.#esc(dist.mean)}</span></div>
            <div class="mlm-kv-row"><span>Disagreements</span><span>showing ${shown} of ${this.#esc(s.disagreement_count)}</span></div>
            ${rows ? `<table class="mlm-table">
                <thead><tr><th>Entity</th><th>Boundary</th><th>Primary</th><th>Shadow</th><th>Score</th></tr></thead>
                <tbody>${rows}</tbody>
            </table>` : ''}
        `;
    }

    // ---- Training jobs ----

    async #fetchTrainingJobs() {
        this.#trainingJobsLoaded = false;
        try {
            const res = await this.fetch(`${this.#baseUrl}/training/jobs`);
            const all = res?.jobs || [];
            this.#trainingJobs = all.filter(j => !this.#selectedModelName || j.model_ref === this.#selectedModelName);
            this.#trainingJobsError = null;
        } catch (err) {
            Logger.error('Failed to fetch training jobs: ', err);
            this.#trainingJobs = [];
            this.#trainingJobsError = err;
        }
        this.#trainingJobsLoaded = true;
        this.#renderTrainingJobs();
    }

    #renderTrainingJobs() {
        const el = this.shadowRoot.getElementById('mlm-training-jobs-body');
        if (!el) return;
        if (!this.#trainingJobsLoaded) { el.innerHTML = '<div class="mlw-placeholder">Loading training jobs…</div>'; return; }
        if (this.#trainingJobsError) { el.innerHTML = `<div class="mlw-error-row">Failed to load training jobs: ${this.#esc(this.#trainingJobsError.message || String(this.#trainingJobsError))}</div>`; return; }
        if (!this.#trainingJobs.length) { el.innerHTML = '<div class="mlw-placeholder">No training jobs for this model.</div>'; return; }
        const rows = this.#trainingJobs.map(j => `
            <tr>
                <td>${this.#esc(j.job_id)}</td>
                <td><span class="mlm-state-badge ${this.#esc(j.state)}">${this.#esc(j.state)}</span></td>
                <td>${this.#esc(j.dataset_ref)}</td>
                <td>${this.#formatTs(j.created_ts)}</td>
                <td>${this.#formatTs(j.updated_ts)}</td>
                <td>${j.error ? this.#esc(j.error) : '—'}</td>
            </tr>`).join('');
        el.innerHTML = `<table class="mlm-table">
            <thead><tr><th>Job</th><th>State</th><th>Dataset</th><th>Created</th><th>Updated</th><th>Error</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>`;
    }

    // POST /training/jobs' request body is not itself specified in spec §4 (only
    // the response/job shape is) — mirrors the response fields as the most
    // grounded inference available.
    async #submitTrainingJob() {
        const datasetRef = this.shadowRoot.getElementById('mlm-training-dataset')?.value;
        try {
            await this.fetch(`${this.#baseUrl}/training/jobs`, {
                method: 'POST',
                payload: {
                    model_ref: this.#selectedModelName,
                    model_version: this.#selectedVersion,
                    dataset_ref: datasetRef || undefined,
                },
            });
            await this.#fetchTrainingJobs();
        } catch (err) {
            Logger.error('Training job submit failed: ', err);
            this.#actionStatus = { ok: false, message: `Training job submit failed: ${err.message || err}` };
            this.#renderActionStatus();
        }
    }
}

customElements.define('hs-ml-models', HSMlModels);
